/**
 * Слой комнат на холсте: заливка, покрытие пола и потолок каждой комнаты.
 *
 * Комнаты вычисляются из графа стен и лежат в самом низу. Клик по комнате
 * выделяет её, а инструментом выделения её можно и потащить (`onRoomDragStart`); инструментом установки с выбранным покрытием из раздела комнаты
 * (пол, гидроизоляция, стяжка, потолок) — назначает его комнате. Как именно
 * рисуется покрытие, знает раздел каталога (FloorShape, CeilingShape и т.д.),
 * здесь только выбор и события. Потолок, если слой включён, ложится поверх пола:
 * на плане потолков смотрят именно на него.
 */
import type { PointerEvent } from "react";
import { CatalogSectionKind } from "@/shared/plan";
import { SelectionKind, Tool, useEditorStore } from "@/widgets/editor";
import { useProjectStore } from "@/shared/store";
import { useFloorsStore } from "@/shared/store/layers/floorsStore";
import { useWaterproofStore } from "@/shared/store/layers/waterproofStore";
import { useScreedStore } from "@/shared/store/layers/screedsStore";
import { useCeilingStore } from "@/shared/store/layers/ceilingStore";
import { useBaseboardStore } from "@/shared/store/layers/baseboardStore";
import { useWallFinishStore } from "@/shared/store/layers/wallFinishStore";
import {
	ceilingItemById,
	ceilingLook,
	floorItemById,
	getFloorPreset,
	screedItemById,
	waterproofItemById,
	finishItemById,
	baseboardItemById,
} from "@/shared/catalog";
import { roomWallFaces, type Room } from "@/widgets/plan";
import { CeilingShape, FloorShape, ScreedShape, WaterproofShape } from "@/widgets/catalog";

interface Props {
	rooms: Room[];
	mmPerPx: number;
	/**
	 * Комнату взяли инструментом выделения — холст решает, тащить её или двигать вид.
	 * `wasSelected` — комната была выделена ещё до этого нажатия
	 */
	onRoomDragStart?: (e: PointerEvent<SVGGElement>, room: Room, wasSelected: boolean) => void;
}

export const RoomsLayer2D = ({ rooms, mmPerPx, onRoomDragStart }: Props) => {
	const floors = useProjectStore((s) => s.project.layers.floors);
	const waterproof = useProjectStore((s) => s.project.layers.waterproof);
	const screed = useProjectStore((s) => s.project.layers.screed);
	const ceiling = useProjectStore((s) => s.project.layers.ceiling);
	const pushHistory = useProjectStore((s) => s.pushHistory);
	const roomsVisible = useProjectStore((s) => s.project.layers.rooms.visible);
	const setRoomFloorMaterial = useFloorsStore((s) => s.setRoomFloorMaterial);
	const setRoomWaterproofMaterial = useWaterproofStore((s) => s.setRoomWaterproofMaterial);
	const setRoomScreedMaterial = useScreedStore((s) => s.setRoomScreedMaterial);
	const setRoomCeilingMaterial = useCeilingStore((s) => s.setRoomCeilingMaterial);
	const setRoomBaseboardMaterial = useBaseboardStore((s) => s.setRoomBaseboardMaterial);
	const setFinish = useWallFinishStore((s) => s.setFinish);
	const graph = useProjectStore((s) => s.project.layers.walls);
	const tool = useEditorStore((s) => s.tool);
	const placing = useEditorStore((s) => s.placing);
	const activeCatalog = useEditorStore((s) => s.activeCatalog);
	const movingFloorPatternRoomId = useEditorStore((s) => s.movingFloorPatternRoomId);
	const setActiveCatalog = useEditorStore((s) => s.setActiveCatalog);
	const setSelection = useEditorStore((s) => s.setSelection);
	const setTool = useEditorStore((s) => s.setTool);

	if (!roomsVisible) return null;

	const onRoomPointerDown = (e: PointerEvent<SVGGElement>, room: Room) => {
		if (e.button !== 0 || e.shiftKey) return;
		const assigningFloor = tool === Tool.Place && placing?.catalog === CatalogSectionKind.Floor;
		const assigningWaterproof =
			tool === Tool.Place && placing?.catalog === CatalogSectionKind.Waterproof;
		const assigningScreed = tool === Tool.Place && placing?.catalog === CatalogSectionKind.Screed;
		const assigningCeiling = tool === Tool.Place && placing?.catalog === CatalogSectionKind.Ceiling;
		const assigningFinish = tool === Tool.Place && placing?.catalog === CatalogSectionKind.Finish;
		const assigningBaseboard =
			tool === Tool.Place && placing?.catalog === CatalogSectionKind.Baseboard;
		const assigning =
			assigningFloor ||
			assigningWaterproof ||
			assigningScreed ||
			assigningCeiling ||
			assigningFinish ||
			assigningBaseboard;
		const hasProperties =
			activeCatalog === CatalogSectionKind.Finish ||
			activeCatalog === CatalogSectionKind.Baseboard ||
			ceiling.visible ||
			floors.visible;
		// Комната перехватывает клик только когда он ей адресован: выделение или назначение
		// покрытия. Иначе событие идёт холсту — там начинают стену, кабель, ставят мебель
		if (tool !== Tool.Select && !assigning) return;
		// Когда для комнаты нечего показать, обычный клик не должен открывать пустую панель.
		if (tool === Tool.Select && !hasProperties) return;
		e.stopPropagation();
		const { selection } = useEditorStore.getState();
		const wasSelected = selection?.kind === SelectionKind.Room && selection.id === room.id;
		// Назначение покрытия — правка чертежа, её можно отменить
		if (assigning) pushHistory();
		if (assigningFloor) {
			// Какое покрытие выбрано — знает каталог, а не холст
			const item = floorItemById(placing.item);
			if (item) {
				const preset = getFloorPreset(item.material);
				setRoomFloorMaterial(room.id, item.material, preset.layout, preset);
			}
		}
		if (assigningWaterproof) {
			const item = waterproofItemById(placing.item);
			if (item) setRoomWaterproofMaterial(room.id, item.material);
		}
		if (assigningScreed) {
			const item = screedItemById(placing.item);
			if (item) setRoomScreedMaterial(room.id, item.material);
		}
		if (assigningCeiling) {
			const item = ceilingItemById(placing.item);
			if (item) setRoomCeilingMaterial(room.id, item.material);
		}
		if (assigningFinish && finishItemById(placing.item)) {
			// Отделка ложится на все стены комнаты — той стороной, что смотрит внутрь
			setFinish(roomWallFaces(graph, room), placing.item);
		}
		if (assigningBaseboard) {
			const item = baseboardItemById(placing.item);
			if (item) setRoomBaseboardMaterial(room.id, item.material);
		}
		// У гидроизоляции и стяжки пока нет редактируемых параметров.
		setSelection(
			assigningWaterproof || assigningScreed ? null : { kind: SelectionKind.Room, id: room.id },
		);
		// Покрытие назначено — назад к выделению, как после установки мебели: иначе следующий
		// клик по полу (двигать раскладку, выбрать другой пол) снова положит покрытие из каталога
		if (assigning) setTool(Tool.Select);
		if (tool === Tool.Select) {
			setActiveCatalog(CatalogSectionKind.Floor);
			onRoomDragStart?.(e, room, wasSelected);
		}
	};

	return (
		<g>
			{rooms.map((room) => {
				const floor = floors.items[room.id];
				const waterproofData = waterproof.items[room.id];
				const screedData = screed.items[room.id];
				const ceilingData = ceiling.items[room.id];
				return (
					<g key={room.id}>
						{waterproof.visible && waterproofData && (
							<WaterproofShape room={room} material={waterproofData.material} />
						)}
						{screed.visible && screedData && (
							<ScreedShape room={room} material={screedData.material} />
						)}
						{floors.visible && <FloorShape room={room} floor={floor} />}
						{ceiling.visible && ceilingData && (
							<CeilingShape room={room} look={ceilingLook(ceilingData)} mmPerPx={mmPerPx} />
						)}
						{/* Невидимый хит-бокс для перехвата кликов */}
						<polygon
							data-role="room-floor"
							data-room-id={room.id}
							points={room.inner.map((p) => `${p.x},${p.y}`).join(" ")}
							fill="transparent"
							stroke="none"
							style={{
								pointerEvents: "auto",
								cursor: movingFloorPatternRoomId === room.id ? "grab" : "pointer",
							}}
							onPointerDown={(e) => onRoomPointerDown(e, room)}
						/>
					</g>
				);
			})}
		</g>
	);
};
